import React from 'react';
import { useDropzone } from 'react-dropzone';
import IconClear from '../images/IconClear.jsx';

const Extensions = {
    bmp: 'image',
    gif: 'image',
    png: 'image',
    jpg: 'image',
    jpeg: 'image',
    webp: 'image',
    tif: 'image',
    tiff: 'image',
    heic: 'image',
    heif: 'image',
    svg: 'image',
    mp3: 'audio',
    wav: 'audio',
    mp4: 'video',
    avi: 'video',

    pdf: 'application',
    doc: 'application',
    docx: 'application',
    vcf: 'application',
    txt: 'text',

    xls: 'application',
    xlsx: 'application',
    csv: 'text'
};

const BaseUpload = ({
    accept,
    multiple = false,
    uploadedFiles = [],
    onFilesUpload,
    newUploadedFiles = [],
    onRemoveNewUploadedFile,
    canUpload = true,
    errors = [],
    isLoading,
    label,
    isRequired,
    reset
}) => {
    const onFilesDrop = (acceptedFiles) => {
        onFilesUpload([]);
        const validFiles = acceptedFiles.filter((file) => {
            const fileExtension = file.name.split('.').pop().toLowerCase();
            return accept.includes(fileExtension);
        });

        if (validFiles.length > 0) {
            onFilesUpload(validFiles);
        }
    };

    const parseAcceptedFile = () => {
        const newAccepted = {};

        accept.forEach((singleAccept) => {
            const extensionType = Extensions[singleAccept];
            if (extensionType) {
                const mimeType = `${extensionType}/${singleAccept}`;
                newAccepted[mimeType] = [`.${singleAccept}`];
            }
        });

        return newAccepted;
    };

    const { fileRejections, getRootProps, getInputProps } = useDropzone({
        accept: parseAcceptedFile(),
        multiple,
        onDrop: onFilesDrop
    });

    const viewLabel = () => {
        if (canUpload) {
            return isRequired ? `* ${label}: ` : `${label}: `;
        }

        return label;
    };

    const formatFileName = (fileName, maxVisibleLength) => {
        const extension = fileName.split('.').pop();
        const nameWithoutExtension = fileName.slice(0, -(extension.length + 1));

        if (nameWithoutExtension.length > maxVisibleLength) {
            const visiblePart = nameWithoutExtension.slice(0, maxVisibleLength - 3);
            return `${visiblePart}...${extension}`;
        }

        return fileName;
    };

    return (
        <div>
            {!isLoading && (label || canUpload) && (
                <div>
                    {label && <span className='block text-sm mb-2'>{viewLabel()}</span>}
                    {canUpload && (
                        <div className='flex flex-wrap gap-2 text-sm'>
                            {accept.map((extension) => (
                                <span
                                    key={extension}
                                    className='bg-theme1-pinkGray text-theme1-text2 px-2 py-1 rounded-lg shadow-sm'
                                >
                                    {`.${extension}`}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!isLoading && canUpload && (
                <div
                    {...getRootProps({
                        className:
                            'p-4 mt-5 border-2 border-dashed rounded-xl border-theme1-text3 bg-theme1-pinkGray text-theme1-text3'
                    })}
                >
                    <input {...(!isLoading && { ...getInputProps() })} />
                    <div className='flex flex-col items-center justify-center text-center'>
                        <div className='text-theme1-text2 mb-2'>
                            <svg
                                className='ml-auto mr-auto'
                                width='35'
                                height='29'
                                viewBox='0 0 35 29'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <g opacity='0.5'>
                                    <path
                                        d='M10.7952 11.4985C10.5 9.49854 11.0804 8.0208 11.667 7.13533C12.2537 6.24987 13.0341 5.50951 13.9493 4.97035C14.8644 4.43119 15.8903 4.10735 16.9491 4.02338C18.0079 3.9394 19.072 4.09749 20.0607 4.48566C21.0494 4.87383 21.9368 5.48191 22.6557 6.26384C23.3746 7.04576 23.9061 7.98103 24.2099 8.9988H25.9999C26.9655 8.99869 27.9054 9.30912 28.6809 9.88424C29.4565 10.4594 30.0265 11.2687 30.3067 12.1926C30.587 13.1165 30.5626 14.1061 30.2373 15.0152C29.9119 15.9242 28.6809 17.9985 25.9999 18.4985M10.7952 8.99854H9C8.13846 8.99843 6.8156 9.30951 6.12358 9.88583C5.43156 10.4622 4.92295 11.2732 4.67288 12.199C4.4228 13.1249 4.44453 14.1166 4.73484 15.0275C5.68168 17.9985 9 18.4985 9.5 18.4985M17.5 11.9985V24.9985M17.5 11.9985L21.5 15.9985M17.5 11.9985L13.5 15.9985'
                                        stroke='#420D14'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                </g>
                            </svg>
                            <span className='text-theme1-text2'>Choose file</span>
                        </div>
                        {errors.length > 0 &&
                            errors.map((error) => (
                                <p key={error} className='text-sm text-theme1-error'>
                                    {error}
                                </p>
                            ))}
                    </div>
                </div>
            )}

            {newUploadedFiles.length > 0 && (
                <div className='rounded-xl mt-4'>
                    <div className='mb-2 font-semibold text-theme1-title2'>Imported files</div>
                    {newUploadedFiles.map((accepted, index) => (
                        <div
                            key={accepted.path}
                            className='flex items-center justify-between p-2 mb-2 border border-theme1-text5 rounded-xl'
                        >
                            <span className='text-sm text-theme1-title2 truncate'>
                                {formatFileName(accepted.name, 50)}
                            </span>
                            <div className='flex items-center space-x-2'>
                                <div onClick={() => onRemoveNewUploadedFile(index)} className='cursor-pointer'>
                                    <IconClear className='fill-theme1-border1 w-4 h-4' />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {fileRejections.length > 0 && (
                <div className='mt-4'>
                    <div className='mb-2 font-semibold text-theme1-error'>Rejected files</div>
                    {fileRejections.map((rejected) => (
                        <div
                            key={rejected.file.name}
                            className='flex items-center justify-between p-2 border border-theme1-error rounded-xl'
                        >
                            <span className='text-sm text-theme1-error truncate'>
                                {formatFileName(rejected.file.name, 46)}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {!canUpload && uploadedFiles.length === 0 && (
                <span className='block text-center text-theme1-disabled'>-</span>
            )}
        </div>
    );
};

export default BaseUpload;
